import React, { HTMLAttributes, ReactNode } from "react";
import { Col, Empty, Row, Spin } from "antd";
import classNames from "classnames";
import styles from "./List.module.css";

type ListGrid = {
  gutter?: number;
  column?: number;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  xxl?: number;
};

export interface ListProps<T> extends HTMLAttributes<HTMLDivElement> {
  dataSource?: T[];
  renderItem?: (item: T, index: number) => ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  loadMore?: ReactNode;
  loading?: boolean;
  locale?: { emptyText?: ReactNode };
  grid?: ListGrid;
  split?: boolean;
  size?: "small" | "medium" | "large" | "default";
  itemLayout?: "horizontal" | "vertical";
}

interface ListItemMetaProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  avatar?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
}

interface ListItemProps extends HTMLAttributes<HTMLDivElement> {
  extra?: ReactNode;
}

function columnSpan(columns?: number) {
  if (!columns) return undefined;
  return Math.max(1, Math.floor(24 / columns));
}

function gridColProps(grid?: ListGrid) {
  if (!grid) return undefined;

  return {
    xs: columnSpan(grid.xs),
    sm: columnSpan(grid.sm),
    md: columnSpan(grid.md),
    lg: columnSpan(grid.lg),
    xl: columnSpan(grid.xl),
    xxl: columnSpan(grid.xxl),
    span: columnSpan(grid.column),
  };
}

function renderContent<T>({
  dataSource,
  renderItem,
  children,
  locale,
  grid,
}: Pick<
  ListProps<T>,
  "dataSource" | "renderItem" | "children" | "locale" | "grid"
>) {
  if (children) return children;

  if (!dataSource?.length) {
    return (
      <div className={styles.emptyText}>
        {locale?.emptyText ?? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      </div>
    );
  }

  const items = dataSource.map((item, index) => (
    <React.Fragment key={index}>{renderItem?.(item, index)}</React.Fragment>
  ));

  if (grid) {
    const colProps = gridColProps(grid);

    return (
      <Row gutter={[grid.gutter ?? 0, grid.gutter ?? 0]}>
        {items.map((item, index) => (
          <Col key={index} {...colProps}>
            {item}
          </Col>
        ))}
      </Row>
    );
  }

  return items;
}

function BaseList<T>({
  dataSource,
  renderItem,
  header,
  footer,
  loadMore,
  loading = false,
  locale,
  grid,
  split = true,
  size,
  itemLayout,
  className,
  children,
  ...props
}: ListProps<T>) {
  const content = renderContent({
    dataSource,
    renderItem,
    children,
    locale,
    grid,
  });

  return (
    <div
      className={classNames(
        styles.root,
        split && styles.split,
        size === "small" && styles.small,
        itemLayout === "vertical" && styles.vertical,
        grid && styles.grid,
        className
      )}
      {...props}
    >
      {header && <div className={styles.header}>{header}</div>}
      <Spin spinning={loading}>
        <div className={styles.items}>{content}</div>
      </Spin>
      {footer && <div className={styles.footer}>{footer}</div>}
      {loadMore}
    </div>
  );
}

function ListItemMeta({
  avatar,
  title,
  description,
  className,
  ...props
}: ListItemMetaProps) {
  return (
    <div className={classNames(styles.meta, className)} {...props}>
      {avatar && <div className={styles.metaAvatar}>{avatar}</div>}
      <div className={styles.metaContent}>
        {title && <h4 className={styles.metaTitle}>{title}</h4>}
        {description && (
          <div className={styles.metaDescription}>{description}</div>
        )}
      </div>
    </div>
  );
}

function ListItem({
  extra,
  className,
  children,
  onClick,
  ...props
}: ListItemProps) {
  return (
    <div
      className={classNames(
        styles.item,
        onClick && styles.clickable,
        className
      )}
      onClick={onClick}
      {...props}
    >
      <div className={styles.itemMain}>{children}</div>
      {extra && <div className={styles.itemExtra}>{extra}</div>}
    </div>
  );
}

type ListItemComponent = typeof ListItem & {
  Meta: typeof ListItemMeta;
};

type ListComponent = (<T>(props: ListProps<T>) => React.ReactElement) & {
  Item: ListItemComponent;
};

ListItem.Meta = ListItemMeta;

export const List = BaseList as ListComponent;
List.Item = ListItem as ListItemComponent;
